import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useMemo } from "react";
import {
  HandoverDetailFormValues,
  HandoverDetailPrefillResponse,
  HandoverDetailResponse,
  useGetHandoverDetailPrefillQuery,
  useGetLatestHandoverDetailQuery,
  useSubmitHandoverDetailMutation,
} from "store/apis/salesCrm";
import { splitNewlineList } from "util/splitNewlineList";

const DEFAULT_FORM_VALUES: HandoverDetailFormValues = {
  customer_name: "",
  closed_by: "",
  cs_assignee: "",
  handover_date: "",
  founder_name: "",
  founder_email: "",
  founder_phone: "",
  founder_phone_country: "US",
  founder_residency: "",
  lead_source: "",
  services_sold: [],
  quote_link: "",
  fireflies_link: "",
  incorporated_state: "",
  incorporated_date: "",
  us_activity: "",
  employees: "",
  bookkeeping_method: "",
  states_of_operation: [],
  foreign_qualification: {},
  shareholding_pattern: "",
  docs_in_hand: [],
  extension_filed: "",
  watch_outs: "",
  confirm_on_call: "",
  attached_document_id: "",
};

// One Yes/No answer per state currently selected in states_of_operation, seeded from the wire-format foreign_qualification list (newline-delimited, "yes" states only).
const buildForeignQualificationMap = (
  states: string[],
  foreignQualificationList: string | null | undefined
): Record<string, string> => {
  const yesStates = new Set(splitNewlineList(foreignQualificationList));
  return Object.fromEntries(
    states.map((state) => [state, yesStates.has(state) ? "yes" : "no"])
  );
};

const toFormValues = (
  data: HandoverDetailPrefillResponse | HandoverDetailResponse | undefined
): Partial<HandoverDetailFormValues> => {
  if (!data) return {};

  const statesOfOperation = splitNewlineList(data.states_of_operation);

  // Some fields (e.g. cs_assignee) can come back `null` instead of omitted, which would override DEFAULT_FORM_VALUES' "" and reach Yup as `null`, so every scalar is coerced back to "" here.
  const nonNullScalars = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value ?? ""])
  );

  return {
    ...nonNullScalars,
    states_of_operation: statesOfOperation,
    docs_in_hand: splitNewlineList(data.docs_in_hand),
    services_sold: splitNewlineList(data.services_sold),
    foreign_qualification: buildForeignQualificationMap(
      statesOfOperation,
      data.foreign_qualification
    ),
  };
};

export const useHandoverDetailsFormData = (
  isOpen: boolean,
  groupId: string
) => {
  const { isAdmin } = useRoleBasedView();
  // Both endpoints are IsInkleTeamAdmin-gated on the backend, so skip for non-admins.
  const { data: latestData, isFetching: isLoadingLatest } =
    useGetLatestHandoverDetailQuery({ groupId }, { skip: !isOpen || !isAdmin });

  const shouldFetchPrefill =
    isOpen && isAdmin && !isLoadingLatest && !latestData;

  const { data: prefillData, isFetching: isLoadingPrefill } =
    useGetHandoverDetailPrefillQuery(
      { groupId },
      { skip: !shouldFetchPrefill }
    );

  const [submitHandoverDetail, { isLoading: isSubmitting }] =
    useSubmitHandoverDetailMutation();

  const isLoadingExistingData =
    isLoadingLatest || (shouldFetchPrefill && isLoadingPrefill);

  const initialValues: HandoverDetailFormValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      ...toFormValues(latestData ?? prefillData),
    }),
    [latestData, prefillData]
  );

  return {
    isLoadingExistingData,
    initialValues,
    submitHandoverDetail,
    isSubmitting,
    submittedByName: latestData?.submitted_by_name ?? null,
    submittedAt: latestData?.submitted_at ?? null,
    // A HandoverDetail row only exists once the group has already moved to Ops (see _create_fresh vs _edit_existing in handover_detail_handler.py) -- so this doubles as "opened from the Ops side, post-handover".
    hasMovedToOps: Boolean(latestData),
  };
};
